import type { ConnectorPresenceEntry } from './services/integration-api.js';
import type {
  Message,
  SetupAction,
  SetupActionKind,
  StreamEvent,
} from './types.js';

/**
 * Every key a `setup_action` frame carries, exactly as the agent and conversation-service send
 * them (snake_case). The same list is asserted on the producer and the transport side, so a field
 * renamed on one side shows up as a failing test, not as a silent drop.
 */
export const SETUP_ACTION_WIRE_FIELDS = [
  'kind',
  'provider',
  'purpose',
  'scope',
  'credential_kind',
  'variable_key',
  'environment_name',
  'project_id',
  'workspace_id',
  'can_complete',
  'admin_hint',
] as const;

/** One `setup_action` frame off the wire; every field is untrusted until narrowed. */
export type SetupActionWire = Partial<
  Record<(typeof SETUP_ACTION_WIRE_FIELDS)[number], unknown>
>;

const KINDS: readonly SetupActionKind[] = [
  'connector',
  'credential',
  'secret_var',
];

/**
 * The `setup_action` fields of one parsed SSE frame, whitelisted: strings stay strings,
 * `can_complete` stays a boolean, and anything else the frame carries is dropped.
 */
export function setupActionFields(
  parsed: Record<string, unknown>,
): StreamEvent {
  const event: StreamEvent = { type: 'setup_action' };
  const fields = event as unknown as Record<string, unknown>;
  for (const field of SETUP_ACTION_WIRE_FIELDS) {
    const value = parsed[field];
    if (field === 'can_complete') {
      if (typeof value === 'boolean') fields[field] = value;
    } else if (typeof value === 'string') {
      fields[field] = value;
    }
  }
  return event;
}

/** A non-empty string, or undefined. */
function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * The card a frame describes, or null for a frame this build cannot show: one with no `kind`, a
 * `kind` it does not know, or no provider. `can_complete` must be true to offer the action: a frame
 * that leaves it out fails closed, and the card shows the admin hint instead.
 */
export function setupActionFromWire(wire: SetupActionWire): SetupAction | null {
  const kind = KINDS.find((candidate) => candidate === wire.kind);
  const provider = text(wire.provider);
  if (!kind || !provider) return null;
  return {
    kind,
    provider,
    purpose: text(wire.purpose),
    scope: text(wire.scope),
    credentialKind: text(wire.credential_kind),
    variableKey: text(wire.variable_key),
    environmentName: text(wire.environment_name),
    projectId: text(wire.project_id),
    workspaceId: text(wire.workspace_id),
    canComplete: wire.can_complete === true,
    adminHint: text(wire.admin_hint),
  };
}

/**
 * The turn's cards with one more added. A card for a kind and provider already on the turn takes
 * that card's place, so a repeated frame updates rather than duplicates it.
 */
export function withSetupAction(
  cards: SetupAction[] | undefined,
  card: SetupAction,
): SetupAction[] {
  const existing = cards ?? [];
  const index = existing.findIndex(
    (candidate) =>
      candidate.kind === card.kind && candidate.provider === card.provider,
  );
  if (index === -1) return [...existing, card];
  return existing.map((candidate, position) =>
    position === index ? card : candidate,
  );
}

/**
 * Fresh rows with the setup cards this client is showing carried over. The cards are not stored on
 * the message row, so a foreground, sync or end-of-turn repaint would otherwise wipe them. A row is
 * matched by id; cards on a row the fresh read does not contain — the streaming bubble before it
 * took the server's id — go to the last assistant row, which is that turn.
 */
export function keepLocalSetupActions(
  fresh: Message[],
  current: Message[],
): Message[] {
  const freshIds = new Set(fresh.map((message) => message.id));
  const byId = new Map<string, SetupAction[]>();
  let orphaned: SetupAction[] | undefined;
  let orphanedFrom: string | undefined;
  for (const message of current) {
    if (!message.setupActions?.length) continue;
    if (freshIds.has(message.id)) byId.set(message.id, message.setupActions);
    else {
      orphaned = message.setupActions;
      orphanedFrom = message.id;
    }
  }
  if (byId.size === 0 && !orphaned) return fresh;
  const lastAssistantIndex = fresh.reduce(
    (found, message, index) => (message.role === 'assistant' ? index : found),
    -1,
  );
  return fresh.map((message, index) => {
    if (message.setupActions?.length) return message;
    const adoptsOrphans = !byId.has(message.id) && index === lastAssistantIndex;
    if (adoptsOrphans && orphanedFrom)
      moveSetupActionState(orphanedFrom, message.id);
    const setupActions =
      byId.get(message.id) ?? (adoptsOrphans ? orphaned : undefined);
    return setupActions ? { ...message, setupActions } : message;
  });
}

/** What a finished card remembers: the opaque id of what it saved, when there is one. */
export interface SetupActionResult {
  credentialId?: string;
}

/** Cards already finished in this tab, keyed by message, kind and provider. */
const finishedCards = new Map<string, SetupActionResult>();
/** Cards whose "connected" message has already been sent. */
const announcedCards = new Set<string>();

/** The identity of one card on one message. */
export function setupActionKey(messageId: string, card: SetupAction): string {
  return JSON.stringify([messageId, card.kind, card.provider]);
}

/** Records that a card's action succeeded; survives a repaint or a remount of the card. */
export function markSetupActionFinished(
  key: string,
  result: SetupActionResult = {},
): void {
  finishedCards.set(key, result);
}

/** What a card that already succeeded in this tab saved, or undefined when it has not succeeded. */
export function setupActionResult(key: string): SetupActionResult | undefined {
  return finishedCards.get(key);
}

/** Claims the one "connected" message a card may send; false once it has been claimed. */
export function claimSetupActionAnnouncement(key: string): boolean {
  if (announcedCards.has(key)) return false;
  announcedCards.add(key);
  return true;
}

/** Gives back a claim whose send failed, so the card announces again on the next idle moment. */
export function releaseSetupActionAnnouncement(key: string): void {
  announcedCards.delete(key);
}

/** What presence said about the provider when the person first pressed the card's button. */
export interface ConnectBaseline {
  connected: boolean;
  connectionIds: string[];
  /** Connections identity reported dead; a reconnect card waits for one of them to recover. */
  deadConnectionIds: string[];
}

/** The baseline a connect is judged against. */
export function connectBaseline(
  entry: ConnectorPresenceEntry | undefined,
): ConnectBaseline {
  const connections = entry?.connections ?? [];
  return {
    connected: entry?.connected === true,
    connectionIds: connections.map((connection) => connection.connectionId),
    deadConnectionIds: connections
      .filter((connection) => connection.needsReconnect === true)
      .map((connection) => connection.connectionId),
  };
}

/**
 * Whether presence now shows the connection the click asked for. A reconnect is done once a
 * connection that was dead is healthy again, or a healthy new one appears. A connect is done on a
 * not-connected to connected change, or when a new connection appears. A provider that was already
 * connected never counts as done just because somebody pressed the button.
 */
export function connectFinished(
  baseline: ConnectBaseline,
  entry: ConnectorPresenceEntry | undefined,
): boolean {
  const connections = entry?.connections ?? [];
  const appeared = connections.some(
    (connection) =>
      !baseline.connectionIds.includes(connection.connectionId) &&
      connection.needsReconnect !== true,
  );
  if (baseline.deadConnectionIds.length > 0) {
    const recovered = connections.some(
      (connection) =>
        baseline.deadConnectionIds.includes(connection.connectionId) &&
        connection.needsReconnect !== true,
    );
    return recovered || appeared;
  }
  return (!baseline.connected && entry?.connected === true) || appeared;
}

/**
 * Carries a message's finished and announced cards over to the id the message is known by now.
 * The streaming bubble starts with a client id and takes the server's id once the stream names it;
 * without this a card finished before the swap would show its action again and could announce twice.
 */
export function moveSetupActionState(fromId: string, toId: string): void {
  if (fromId === toId) return;
  /** The same card's key under the new message id, or null for a card of another message. */
  const renamed = (key: string): string | null => {
    const [messageId, kind, provider] = JSON.parse(key) as [
      string,
      string,
      string,
    ];
    return messageId === fromId ? JSON.stringify([toId, kind, provider]) : null;
  };
  for (const [key, result] of [...finishedCards]) {
    const next = renamed(key);
    if (next) finishedCards.set(next, result);
  }
  for (const key of [...announcedCards]) {
    const next = renamed(key);
    if (next) announcedCards.add(next);
  }
}

/** Clears the finished and announced cards. For tests. */
export function resetSetupActionRegistry(): void {
  finishedCards.clear();
  announcedCards.clear();
}
